import type { SupabaseClient } from '@supabase/supabase-js';
import type { BillingIdentityLink } from '@/lib/billing/billing-identity';

/** Load billing_identity_links for the signed-in user (client or server Supabase). */
export async function loadBillingIdentityForUser(
  supabase: SupabaseClient,
  userId: string
): Promise<{ row: BillingIdentityLink | null; error: string | null }> {
  const { data, error } = await supabase
    .from('billing_identity_links')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (!error) {
    return { row: (data as BillingIdentityLink) ?? null, error: null };
  }

  return { row: null, error: error.message };
}

/** Browser fallback when direct Supabase read fails (still uses user session, no service role). */
export async function fetchBillingIdentityViaApi(): Promise<{
  row: BillingIdentityLink | null;
  tier: string;
  subscriptionStatus: string;
  userId: string | null;
  error: string | null;
}> {
  try {
    const res = await fetch('/api/billing/plan-context', {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    const body = await res.json();
    if (!res.ok) {
      return {
        row: null,
        tier: 'free',
        subscriptionStatus: 'free',
        userId: null,
        error: body.error || res.statusText,
      };
    }
    return {
      row: body.billingIdentity ?? null,
      tier: body.tier ?? 'free',
      subscriptionStatus: body.subscriptionStatus ?? 'free',
      userId: body.userId ?? null,
      error: body.billingQueryError ?? null,
    };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'plan-context fetch failed';
    return {
      row: null,
      tier: 'free',
      subscriptionStatus: 'free',
      userId: null,
      error: message,
    };
  }
}
